import { useState } from 'react'
import TaskItem from './TaskItem'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Filter, 
  ArrowUpDown, 
  CheckCircle2, 
  Circle, 
  Clock,
  AlertCircle,
  Search
} from 'lucide-react'

function TaskList({ tasks, emptyMessage = 'No tasks found' }) {
  const [filter, setFilter] = useState('all')
  const [sortBy, setSortBy] = useState('priority')
  const [searchQuery, setSearchQuery] = useState('')

  if (tasks.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="empty-state py-16"
      >
        <motion.div
          animate={{ 
            y: [0, -10, 0],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="w-24 h-24 rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 flex items-center justify-center mb-6"
        >
          <CheckCircle2 className="w-12 h-12 text-indigo-500 dark:text-indigo-400" />
        </motion.div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {emptyMessage}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-sm">
          Add a new task to get started with your productivity journey
        </p>
      </motion.div>
    )
  }

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    // Search filter
    if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !task.description?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false
    }
    
    // Status filter
    if (filter === 'all') return true
    if (filter === 'pending') return task.status !== 'completed'
    if (filter === 'completed') return task.status === 'completed'
    if (filter === 'high') return task.priority === 'high'
    return true
  })

  // Sort tasks
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (sortBy === 'priority') {
      const priorityOrder = { high: 0, medium: 1, low: 2 }
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority]
      }
    }
    if (sortBy === 'dueDate') {
      if (!a.dueDate) return 1
      if (!b.dueDate) return -1
      return new Date(a.dueDate) - new Date(b.dueDate)
    }
    if (sortBy === 'created') {
      return new Date(b.createdAt) - new Date(a.createdAt)
    }
    return 0
  })

  // Group by status
  const pendingTasks = sortedTasks.filter(t => t.status !== 'completed')
  const completedTasks = sortedTasks.filter(t => t.status === 'completed')

  const filterOptions = [
    { key: 'all', label: 'All', icon: Filter, count: tasks.length },
    { key: 'pending', label: 'Pending', icon: Circle, count: tasks.filter(t => t.status !== 'completed').length },
    { key: 'completed', label: 'Done', icon: CheckCircle2, count: tasks.filter(t => t.status === 'completed').length },
    { key: 'high', label: 'High Priority', icon: AlertCircle, count: tasks.filter(t => t.priority === 'high').length },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <div className="space-y-5">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tasks..."
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>
        
        {/* Sort */}
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4 text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="priority">Priority</option>
            <option value="dueDate">Due Date</option>
            <option value="created">Created</option>
          </select>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {filterOptions.map((option) => (
          <motion.button
            key={option.key}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setFilter(option.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              filter === option.key
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800'
                : 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <option.icon className="w-4 h-4" />
            {option.label}
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              filter === option.key
                ? 'bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}>
              {option.count}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Task count */}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Showing <span className="font-medium text-gray-900 dark:text-white">{filteredTasks.length}</span> of{' '}
        <span className="font-medium text-gray-900 dark:text-white">{tasks.length}</span> tasks
      </p>

      {/* Task Lists */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-3"
      >
        <AnimatePresence mode="popLayout">
          {/* Pending tasks */}
          {pendingTasks.map(task => (
            <motion.div key={task.id} variants={itemVariants} layout>
              <TaskItem task={task} />
            </motion.div>
          ))}

          {/* Completed tasks */}
          {completedTasks.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Completed ({completedTasks.length})
              </h4>
              <div className="space-y-2 opacity-75">
                {completedTasks.map(task => (
                  <motion.div key={task.id} variants={itemVariants} layout>
                    <TaskItem task={task} />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {filteredTasks.length === 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <p className="text-gray-500 dark:text-gray-400">
            No tasks match the selected filters
          </p>
          <button
            onClick={() => { setFilter('all'); setSearchQuery('') }}
            className="mt-2 text-indigo-600 dark:text-indigo-400 hover:underline text-sm"
          >
            Clear filters
          </button>
        </motion.div>
      )}
    </div>
  )
}

export default TaskList
